import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Brain, MessageSquare, Upload, Trash2, Search, RefreshCw, FileText, Save, RotateCcw, BookOpen, Layers, Home, TrendingUp, UserCheck, Calendar, Check, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_ICONS = { Home, TrendingUp, UserCheck, Calendar };
const PACK_COLORS = {
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', activeBg: 'bg-blue-500/20', ring: 'ring-blue-500/40' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', activeBg: 'bg-amber-500/20', ring: 'ring-amber-500/40' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', activeBg: 'bg-emerald-500/20', ring: 'ring-emerald-500/40' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400', activeBg: 'bg-violet-500/20', ring: 'ring-violet-500/40' },
};

export default function AIConfigPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('role-packs');

  return (
    <div className="space-y-6" data-testid="ai-config-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">AI Configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">Customize your AI assistant's behavior, prompts, and knowledge base</p>
      </div>

      <div className="flex gap-1 border-b border-border pb-px overflow-x-auto">
        {[
          { id: 'role-packs', label: 'Role Packs', icon: Layers },
          { id: 'prompts', label: 'Prompts & Greetings', icon: MessageSquare },
          { id: 'knowledge', label: 'Knowledge Brain', icon: Brain },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              tab === t.id ? 'text-emerald-400 border-b-2 border-emerald-400 bg-card' : 'text-muted-foreground hover:text-foreground'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'role-packs' && <RolePacksTab headers={headers} onApplied={() => setTab('prompts')} />}
      {tab === 'prompts' && <PromptsTab headers={headers} />}
      {tab === 'knowledge' && <KnowledgeTab headers={headers} />}
    </div>
  );
}


function RolePacksTab({ headers, onApplied }) {
  const [packs, setPacks] = useState([]);
  const [activePack, setActivePack] = useState(null);
  const [applying, setApplying] = useState(null);

  const fetchPacks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-config/role-packs`, { headers });
      setPacks(data.packs || []);
      setActivePack(data.active_pack || null);
    } catch { toast.error('Failed to load role packs'); }
  }, []);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const applyPack = async (packId) => {
    setApplying(packId);
    try {
      const { data } = await axios.post(`${API}/ai-config/role-packs/apply`, { pack_id: packId }, { headers });
      setActivePack(data.pack_id);
      toast.success(`${data.pack_name} applied — all prompts updated`);
      if (onApplied) onApplied();
    } catch { toast.error('Failed to apply role pack'); }
    finally { setApplying(null); }
  };

  if (!packs.length) return <div className="text-muted-foreground text-sm py-8 text-center"><RefreshCw className="animate-spin inline mr-2" size={14} />Loading role packs...</div>;

  return (
    <div className="space-y-4" data-testid="role-packs-tab">
      <p className="text-sm text-muted-foreground">Pre-configured AI behaviors for different workflows. Applying a pack overwrites your current prompts.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {packs.map(pack => {
          const Icon = PACK_ICONS[pack.icon] || Layers;
          const c = PACK_COLORS[pack.color] || PACK_COLORS.emerald;
          const isActive = pack.id === activePack;
          return (
            <div key={pack.id} className={`rounded-xl border p-5 transition-all ${isActive ? `${c.activeBg} ${c.border} ring-1 ${c.ring}` : 'bg-card border-border hover:border-border'}`} data-testid={`role-pack-${pack.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}>
                    <Icon size={20} className={c.text} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{pack.name}</h3>
                    {isActive && <span className="text-[10px] font-medium text-emerald-400 flex items-center gap-1 mt-0.5"><Check size={10} /> Active</span>}
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">{pack.description}</p>
              <button
                onClick={() => applyPack(pack.id)}
                disabled={applying === pack.id}
                className={`w-full py-2 rounded-lg text-xs font-medium transition-colors ${isActive ? 'bg-muted text-foreground border border-border hover:bg-accent' : `${c.bg} ${c.text} border ${c.border} hover:opacity-80`}`}
                data-testid={`apply-pack-${pack.id}`}
              >
                {applying === pack.id ? 'Applying...' : isActive ? 'Re-apply Pack' : 'Apply Pack'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function AIGenBtn({ field, headers, onDone }) {
  const [loading, setLoading] = useState(false);
  const go = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai-tools/generate`, { field }, { headers });
      onDone(data.generated);
      toast.success('AI draft generated');
    } catch { toast.error('Generation failed'); }
    finally { setLoading(false); }
  };
  return (
    <button type="button" onClick={go} disabled={loading}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-400 hover:text-primary transition-colors disabled:opacity-50"
      data-testid={`ai-gen-${field}`}>
      {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
      Generate with AI
    </button>
  );
}


function PromptsTab({ headers }) {
  const [prompts, setPrompts] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPrompts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-config/prompts`, { headers });
      setPrompts(data);
    } catch { toast.error('Failed to load prompts'); }
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai-config/prompts`, prompts, { headers });
      toast.success('Prompts saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset all prompts to defaults?')) return;
    try {
      const { data } = await axios.post(`${API}/ai-config/prompts/reset`, {}, { headers });
      setPrompts({ ...data.defaults, agent_id: prompts?.agent_id });
      toast.success('Prompts reset to defaults');
    } catch { toast.error('Failed to reset'); }
  };

  if (!prompts) return <div className="text-muted-foreground text-sm py-8 text-center"><RefreshCw className="animate-spin inline mr-2" size={14} />Loading prompts...</div>;

  const fields = [
    { key: 'inbound_greeting', label: 'Inbound Greeting', desc: 'What the AI says when answering a call. Use {assistant_name}, {agent_name}, {brokerage_text} as variables.', rows: 2 },
    { key: 'outbound_greeting', label: 'Outbound Greeting', desc: 'Opening line for outbound AI calls. Use {lead_name}, {assistant_name}, {agent_name}.', rows: 2 },
    { key: 'buyer_prompt', label: 'Buyer Prompt', desc: 'Instructions for handling buyer inquiries.', rows: 3 },
    { key: 'seller_prompt', label: 'Seller Prompt', desc: 'Instructions for handling seller inquiries.', rows: 3 },
    { key: 'objection_handling', label: 'Objection Handling', desc: 'How to handle caller hesitation or pushback.', rows: 3 },
    { key: 'appointment_rules', label: 'Appointment Booking Rules', desc: 'Rules for scheduling appointments during calls.', rows: 3 },
    { key: 'transfer_rules', label: 'Transfer Rules', desc: 'When should the AI transfer the call to you?', rows: 3 },
    { key: 'voicemail_behavior', label: 'Voicemail Behavior', desc: 'What happens when a call goes to voicemail.', rows: 2 },
    { key: 'custom_instructions', label: 'Custom Instructions', desc: 'Any additional instructions for your AI assistant.', rows: 3 },
    { key: 'personality_notes', label: 'Personality Notes', desc: 'Describe the tone and personality you want.', rows: 2 },
    { key: 'brokerage_knowledge', label: 'Brokerage Knowledge', desc: 'Info about your brokerage (services, specialties, areas). Automatically added to AI knowledge base.', rows: 4 },
  ];

  return (
    <div className="space-y-4" data-testid="prompts-tab">
      <div className="flex justify-end gap-2">
        <button onClick={reset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground border border-border rounded-lg hover:text-foreground hover:border-border transition-colors" data-testid="reset-prompts-btn">
          <RotateCcw size={12} /> Reset Defaults
        </button>
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 text-white text-xs rounded-lg font-medium hover:bg-emerald-500 disabled:opacity-50 transition-colors" data-testid="save-prompts-btn">
          <Save size={12} /> {saving ? 'Saving...' : 'Save All'}
        </button>
      </div>

      {fields.map(f => (
        <div key={f.key} className="rounded-lg border border-border bg-card/40 p-4">
          <div className="flex items-center justify-between mb-0.5">
            <label className="block text-sm font-medium text-foreground">{f.label}</label>
            <AIGenBtn field={f.key} headers={headers} onDone={(v) => setPrompts(p => ({ ...p, [f.key]: v }))} />
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">{f.desc}</p>
          <textarea
            value={prompts[f.key] || ''}
            onChange={e => setPrompts(p => ({ ...p, [f.key]: e.target.value }))}
            rows={f.rows}
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-y"
            data-testid={`prompt-${f.key}`}
          />
        </div>
      ))}
    </div>
  );
}


function KnowledgeTab({ headers }) {
  const [docs, setDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showAddText, setShowAddText] = useState(false);
  const [textForm, setTextForm] = useState({ title: '', content: '', doc_type: 'custom' });
  const [uploading, setUploading] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-config/knowledge`, { headers });
      setDocs(data.documents || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const { data } = await axios.post(`${API}/ai-config/knowledge/search`, { query: searchQuery }, { headers });
      setSearchResults(data.results || []);
    } catch { toast.error('Search failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);
    formData.append('doc_type', 'custom');
    try {
      await axios.post(`${API}/ai-config/knowledge/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded and indexed');
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const addText = async () => {
    if (!textForm.title || !textForm.content) return;
    try {
      await axios.post(`${API}/ai-config/knowledge/text`, textForm, { headers });
      toast.success('Knowledge added');
      setShowAddText(false);
      setTextForm({ title: '', content: '', doc_type: 'custom' });
      fetchDocs();
    } catch { toast.error('Failed to add'); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Delete this document from the knowledge base?')) return;
    try {
      await axios.delete(`${API}/ai-config/knowledge/${docId}`, { headers });
      setDocs(d => d.filter(doc => doc.id !== docId));
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const typeColors = {
    faq: 'bg-blue-500/10 text-blue-400',
    process: 'bg-purple-500/10 text-purple-400',
    mortgage: 'bg-amber-500/10 text-amber-400',
    negotiation: 'bg-rose-500/10 text-rose-400',
    brokerage: 'bg-emerald-500/10 text-emerald-400',
    custom: 'bg-secondary text-muted-foreground',
  };

  return (
    <div className="space-y-5" data-testid="knowledge-tab">
      {/* Actions bar */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 cursor-pointer transition-colors" data-testid="upload-doc-btn">
          <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Document'}
          <input type="file" accept=".txt,.md,.csv" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
        <button onClick={() => setShowAddText(!showAddText)} className="flex items-center gap-2 px-4 py-2 border border-border text-foreground rounded-lg text-sm hover:border-border transition-colors" data-testid="add-text-btn">
          <FileText size={14} /> Add Text
        </button>
      </div>

      {/* Add text form */}
      {showAddText && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="add-text-form">
          <input value={textForm.title} onChange={e => setTextForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-emerald-500" data-testid="text-title" />
          <textarea value={textForm.content} onChange={e => setTextForm(p => ({ ...p, content: e.target.value }))} placeholder="Knowledge content..." rows={5} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-emerald-500 resize-y" data-testid="text-content" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAddText(false)} className="px-3 py-1.5 text-xs text-muted-foreground">Cancel</button>
            <button onClick={addText} className="px-4 py-1.5 bg-emerald-600 text-white text-xs rounded-lg font-medium hover:bg-emerald-500" data-testid="save-text-btn">Save & Index</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search knowledge base..." className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-emerald-500" data-testid="knowledge-search" />
        <button onClick={handleSearch} className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground text-sm hover:border-border transition-colors" data-testid="search-btn">
          <Search size={14} />
        </button>
      </div>

      {/* Search results */}
      {searchResults && (
        <div className="space-y-2" data-testid="search-results">
          <h3 className="text-xs font-medium text-muted-foreground">{searchResults.length} results</h3>
          {searchResults.map((r, i) => (
            <div key={i} className="rounded-lg border border-border bg-card/40 p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-foreground">{r.title}</span>
                <span className="text-[10px] text-muted-foreground">Score: {r.score}</span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-3">{r.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Documents list */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <BookOpen size={14} /> Knowledge Documents ({docs.length})
        </h3>
        {docs.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center" data-testid="no-docs">
            <Brain className="mx-auto text-muted-foreground mb-3" size={32} />
            <p className="text-sm text-muted-foreground">No custom documents yet. Pre-loaded real estate knowledge is active.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="docs-list">
            {docs.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 rounded-lg border border-border bg-card/40 p-3 hover:border-border transition-colors">
                <FileText size={16} className="shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{doc.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${typeColors[doc.doc_type] || typeColors.custom}`}>{doc.doc_type}</span>
                    {doc.agent_id === '__global__' && <span className="text-[10px] text-muted-foreground">Global</span>}
                  </div>
                  <span className="text-[11px] text-muted-foreground">{doc.chunk_count} chunks &middot; {(doc.char_count / 1000).toFixed(1)}k chars</span>
                </div>
                {doc.agent_id !== '__global__' && (
                  <button onClick={() => deleteDoc(doc.id)} className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors" data-testid={`delete-doc-${doc.id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
